import { history } from 'umi';
import { makeObservable, action, observable, runInAction } from 'mobx';
import { BaseService } from '../base';
import UserInfo from './api.interface';
import { UserInfoModel } from './userInfo.model';
import { CacheStatusModel } from './cacheStatus.model';
import { ToggleStatusModel } from './toggleStatus.model';
import {
  RISK_REJECT_CODE,
  BIZ_TYPE,
  LOCK_TYPE,
  CodeType,
  UC_USER_STATUS,
} from './userInfo.interface';
import insight from '@/utils/v2/insight';
import { getLoginCode, goWsaUrl } from '@/utils/v2/authcode';
import { isShopeeSellerApp } from '@/utils/v2/device';
import { NO_PERMISSION } from '@/utils/v2/request';

const UNACTIVE = [30001, 30002, 30003, 90113, 90011]; // 黑名单用户错误码

export class UserInfoService extends BaseService {
  @observable userInfo: UserInfoModel = new UserInfoModel();

  /** Cache 用户状态(独立 model)，数据来源 query_user_cache_status */
  @observable cacheStatus: CacheStatusModel = new CacheStatusModel();

  /** FES 开关状态(独立 model)，数据来源 query_user_toggle_status(统一来源，不再依赖 homepage info) */
  @observable toggleStatus: ToggleStatusModel = new ToggleStatusModel();

  constructor() {
    super();
    makeObservable(this);
  }

  isUnactivated(userInfo: UserInfoModel) {
    const userInfoState = userInfo;
    const { userStatus } = userInfoState;

    return userStatus < 20;
  }

  isFrozen(userInfo: UserInfoModel) {
    const { userStatus } = userInfo;
    return userStatus >= 30;
  }

  isContractUnsigned(userInfo: UserInfoModel) {
    const { userStatus } = userInfo;
    return userStatus === UC_USER_STATUS.CONTRACT_NOT_SIGNED;
  }

  /** / PRD：以 UC 返回的 user_status 判定冻结类型，不再依赖 lock_info_list 是否有值 */
  isCustomedTypeFrozen(userInfo: UserInfoModel, type: UC_USER_STATUS) {
    const { userStatus } = userInfo;
    return this.isFrozen(userInfo) && userStatus === type;
  }

  isDPDFrozen(userInfo: UserInfoModel) {
    const { userStatus } = userInfo;
    if (!this.isFrozen(userInfo)) {
      return false;
    }
    return (
      userStatus === UC_USER_STATUS.DPD_FROZEN_311 ||
      userStatus === UC_USER_STATUS.DPD_FROZEN_320 ||
      userStatus === UC_USER_STATUS.DPD_FROZEN_321
    );
  }

  isWhatTypeFrozen(userInfo: UserInfoModel, type: LOCK_TYPE) {
    const { lockInfoList } = userInfo;

    return (
      this.isFrozen(userInfo) &&
      lockInfoList?.some(
        (item) => item?.biz_type === BIZ_TYPE.FastEscrow && item?.lock_type === type,
      )
    );
  }

  // 贷中贷后只在存量用户提示updateInfo和路由控制用到UC拒绝码。
  isInActivationCoolTime(userInfo: UserInfoModel) {
    const userInfoState: UserInfoModel = userInfo;
    const { riskRejectCode, coolDownEndTime } = userInfoState;

    return !!(
      this.isUnactivated(userInfoState) &&
      [
        RISK_REJECT_CODE.FRAUD_COOL_DOWN1,
        RISK_REJECT_CODE.FRAUD_COOL_DOWN2,
        RISK_REJECT_CODE.UNDERWRITING,
        RISK_REJECT_CODE.VERIFICATION_COOL_DOWN,
      ].includes(riskRejectCode) &&
      coolDownEndTime
    );
  }

  isCDDWillExpire(userInfo: UserInfoModel) {
    const { userStatus, riskRejectCode } = userInfo;
    return (
      userStatus === UC_USER_STATUS.NORMAL && riskRejectCode === RISK_REJECT_CODE.KYC_WILL_EXPIRE
    );
  }

  isCDDExpired(userInfo: UserInfoModel) {
    const { userStatus, riskRejectCode } = userInfo;
    return (
      userStatus === UC_USER_STATUS.UPDATEINFO && riskRejectCode === RISK_REJECT_CODE.KYC_EXPIRED
    );
  }

  @action
  async getUserInfo() {
    const authCodeResp = await getLoginCode();

    if (!authCodeResp.token) {
      // errcode=0，表示是RN没取到，此时要跳转wsa url兜底；否则表示wsa也没取到，展示错误页就行
      authCodeResp.errorCode === 0 ? goWsaUrl() : history.replace({ pathname: '/exception' });
      return false;
    }

    const requestHeaderParams =
      authCodeResp.type === CodeType.AuthCode
        ? { 'X-Kredit-Req-AuthCode': authCodeResp.token }
        : { 'X-Kredit-Req-Nonce': authCodeResp.token };

    const { code: loginCode, result: loginResult } = await this.post(UserInfo.API.login, {
      headers: requestHeaderParams,
    });

    if (loginCode === NO_PERMISSION && isShopeeSellerApp()) {
      // 使用 replace 而不是 push，避免在历史记录中留下错误页面
      history.replace({ pathname: '/no-permission' });
      return false;
    }

    if (loginCode === 0) {
      const { code, result } = await this.post(UserInfo.API.get_user_info);
      if (code === 0) {
        const userInfo = {
          userId: loginResult?.user_id ?? '',
          lockInfoList: result.lock_info_list || [],
          userStatus: result.user_status,
          userTotalStatus: result.user_total_status,
          isStockUser: result.is_stock_user,
          screenStatus: result.screen_status,
          kycStatus: result.kyc_status,
          fraudStatus: result.fraud_status,
          overdueStatus: result.overdue_status,
          riskRejectCode: result.risk_reject_code,
          coolDownEndTime: result.cool_down_end_time,
          totalLimit: result.total_limit,
          availableLimit: result.available_limit,
          isFirstIn: result.is_first_in,
        } as UserInfoModel;

        localStorage.setItem('platform_user_id', result.platform_user_id);

        insight.loginReport({
          status: 'success',
          type: authCodeResp.type === CodeType.AuthCode ? 'auth_code' : 'nonce_code',
        });

        // UC DR Frozen
        if (this.isWhatTypeFrozen(userInfo, LOCK_TYPE.DR_DISABLE_USER)) {
          history.push({ pathname: '/exception', state: { status: 'drfrozen' } });
          return false;
        }

        // 拿到用户信息后、写入 this.userInfo 前，已激活 fast 的用户才查询 Cache 状态
        // (未激活会被重定向到 UC 激活页，不应调用该接口)
        if (!this.isUnactivated(userInfo)) {
          await this.getCacheStatus();
        }

        runInAction(() => {
          this.userInfo = {
            ...userInfo,
            isUnactivated: this.isUnactivated(userInfo),
            isFrozen: this.isFrozen(userInfo),
            isKYCFrozenWithUpdateInfo: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.UPDATEINFO,
            ),
            isKYCFrozenWithUpdateInfoPending: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.FREEZE_UPDATE_INFO_PENDING,
            ),
            isKYCFrozenWithoutUpdateInfo: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.KYC_FROZEN,
            ),
            isFraudFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.FRAUD),
            isOverdueFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.OVERDUE),
            isSellerFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.SELLER_FROZEN),
            isCNPJFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CNPJ_FROZEN),
            isICFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.IC_FROZEN),
            isFSKYCFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.FS_KYC_FROZEN),
            isUnderFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.UNDER_WRITING_FREEZE),
            isPaymentRejectCoolDownFrozen: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.PAYMENT_REJECT_FROZEN,
            ),
            isTemporaryUnderwritingFrozen: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.TEMPORARY_UNDER_WRITING_FROZEN,
            ),
            isSPPFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.SPP_FROZEN),
            isSPPUnlinkFrozen: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.SPP_UNLINK_FROZEN,
            ),
            isContractFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CONTRACT_FROZEN),
            isCICFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CIC_FROZEN),
            isOtherCreditOverdueFrozen: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.OTHER_CREDIT_PRODUCT_OVERDUE_FROZEN,
            ),
            isDPDFrozen: this.isDPDFrozen(userInfo),
            isShopeeAccountFrozen: this.isCustomedTypeFrozen(
              userInfo,
              UC_USER_STATUS.SHOPEE_STATUS_ACCOUNT_FROZEN,
            ),
            isContractUnsigned: this.isContractUnsigned(userInfo),
            isCDDWillExpire: this.isCDDWillExpire(userInfo),
            isCDDExpired: this.isCDDExpired(userInfo),
            islogin: true,
          };
        });

        return true;
      }
      insight.loginReport({
        status: 'error',
        type: authCodeResp.type === CodeType.AuthCode ? 'auth_code' : 'nonce_code',
      });

      console.error('调用获取用户信息接口报错');
      return false;
    }

    if (UNACTIVE.includes(loginCode as number)) {
      console.error('用户未被邀请');
      history.push({ pathname: '/exception', state: { status: 'uninvite' } });
      return false;
    }

    console.error('调用登录接口报错');
    return false;
  }

  @action
  async updateUserInfo() {
    const { code, result } = await this.post(UserInfo.API.get_user_info);
    if (code !== 0) {
      return false;
    }
    localStorage.setItem('platform_user_id', result.platform_user_id);

    const userInfo = {
      lockInfoList: result.lock_info_list || [],
      userStatus: result.user_status,
      userTotalStatus: result.user_total_status,
      isStockUser: result.is_stock_user,
      screenStatus: result.screen_status,
      kycStatus: result.kyc_status,
      fraudStatus: result.fraud_status,
      overdueStatus: result.overdue_status,
      riskRejectCode: result.risk_reject_code,
      coolDownEndTime: result.cool_down_end_time,
      totalLimit: result.total_limit,
      availableLimit: result.available_limit,
      isFirstIn: result.is_first_in,
    } as UserInfoModel;

    // UC DR Frozen
    if (this.isWhatTypeFrozen(userInfo, LOCK_TYPE.DR_DISABLE_USER)) {
      history.push({ pathname: '/exception', state: { status: 'drfrozen' } });
      // 这里返回true表示不需再请求后续接口了
      return true;
    }

    // 拿到用户信息后、写入 this.userInfo 前，已激活 fast 的用户才查询 Cache 状态
    // (未激活会被重定向到 UC 激活页，不应调用该接口)
    if (!this.isUnactivated(userInfo)) {
      await this.getCacheStatus();
    }

    this.userInfo = {
      ...userInfo,
      isUnactivated: this.isUnactivated(userInfo),
      isFrozen: this.isFrozen(userInfo),
      isKYCFrozenWithUpdateInfo: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.UPDATEINFO),
      isKYCFrozenWithUpdateInfoPending: this.isCustomedTypeFrozen(
        userInfo,
        UC_USER_STATUS.FREEZE_UPDATE_INFO_PENDING,
      ),
      isKYCFrozenWithoutUpdateInfo: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.KYC_FROZEN),
      isFraudFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.FRAUD),
      isOverdueFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.OVERDUE),
      isSellerFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.SELLER_FROZEN),
      isCNPJFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CNPJ_FROZEN),
      isICFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.IC_FROZEN),
      isFSKYCFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.FS_KYC_FROZEN),
      isUnderFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.UNDER_WRITING_FREEZE),
      isPaymentRejectCoolDownFrozen: this.isCustomedTypeFrozen(
        userInfo,
        UC_USER_STATUS.PAYMENT_REJECT_FROZEN,
      ),
      isTemporaryUnderwritingFrozen: this.isCustomedTypeFrozen(
        userInfo,
        UC_USER_STATUS.TEMPORARY_UNDER_WRITING_FROZEN,
      ),
      isSPPFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.SPP_FROZEN),
      isSPPUnlinkFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.SPP_UNLINK_FROZEN),
      isContractFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CONTRACT_FROZEN),
      isCICFrozen: this.isCustomedTypeFrozen(userInfo, UC_USER_STATUS.CIC_FROZEN),
      isOtherCreditOverdueFrozen: this.isCustomedTypeFrozen(
        userInfo,
        UC_USER_STATUS.OTHER_CREDIT_PRODUCT_OVERDUE_FROZEN,
      ),
      isDPDFrozen: this.isDPDFrozen(userInfo),
      isShopeeAccountFrozen: this.isCustomedTypeFrozen(
        userInfo,
        UC_USER_STATUS.SHOPEE_STATUS_ACCOUNT_FROZEN,
      ),
      isContractUnsigned: this.isContractUnsigned(userInfo),
      isCDDWillExpire: this.isCDDWillExpire(userInfo),
      isCDDExpired: this.isCDDExpired(userInfo),
      islogin: true,
    };

    return true;
  }

  /**
   * @description 查询并更新 Cache 状态(QueryUserCacheStatus，由 fastescrow 提供)
   * - is_cache_user：是否 Cache 用户(已签新合约 + 白名单)，控制是否展示 Cache UI
   * 注意：is_eligible_cache / is_popup_for_resign 已由后端移入 query_homepage_info_new(BFF 6.1)，本接口现仅返回 is_cache_user。
   * 仅 ID 市场有 Cache 功能；且仅在用户已激活 fast 时由调用方判断后调用。
   */
  @action
  async getCacheStatus() {
    // userInfo 为各市场共享的 store，Cache 功能仅 ID 市场有，其他市场直接返回
    if (BIZ_COUNTRY !== 'id') {
      return false;
    }
    const { code, result } = await this.post(UserInfo.API.query_user_cache_status);
    if (code !== 0) {
      // 查询失败就无从判断是否 cache 用户，不能静默降级成非 cache UI，直接走异常页(默认 busy)
      history.replace({ pathname: '/exception' });
      return false;
    }
    runInAction(() => {
      this.cacheStatus = {
        isCacheUser: !!result?.is_cache_user,
      } as CacheStatusModel;
    });
    return true;
  }

  /**
   * @description 上报「签合同弹窗已展示」(UpdateCachePopupStatus，BFF 6.15)，
   * 供后端控制弹窗展示频率(每月一次)。在签合同弹窗「关闭时」调用(而非打开即调用)。
   */
  @action
  async updateCachePopupStatus() {
    await this.post(UserInfo.API.update_cache_popup_status);
  }

  /**
   * @description 查询并更新 FES 开关状态(QueryUserToggleStatus，由 fastescrow 提供)
   * toggle_status：0 CREATED / 1 DISABLED / 2 ENABLED(非 ENABLED 一律视为未开启)。
   * 统一从该接口取开关状态，不再依赖 homepage info 里的 toggle_status。
   * 由页面在需要处调用(参考原 homepage/setting 用 getHomeInfo 的时机)。
   */
  @action
  async getToggleStatus() {
    const { code, result } = await this.post(UserInfo.API.query_user_toggle_status);
    if (code !== 0) {
      return false;
    }
    runInAction(() => {
      this.toggleStatus = {
        status: result?.toggle_status,
      } as ToggleStatusModel;
    });
    return true;
  }

  /**
   *
   *@description 上报insight
   */
  async requestInsight(requestData: Record<string, any>) {
    await this.pureRequest({
      url: UserInfo.API.insight_fe_chat,
      method: 'POST',
      requestData: requestData ?? {},
    });
  }
}
